import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import api, { API } from "../lib/api";

// Buyer/recipient-facing trust badge: fetches the watertight / 0.4mm-nozzle report for a ready duck.
// `share` uses the public /share endpoint (no auth) for the shared duck page & passport.
export const PrintReadyBadge = ({ creationId, share = false, notice = null }) => {
  const [rep, setRep] = useState(null); // null=loading, false=unavailable

  useEffect(() => {
    let alive = true;
    setRep(null);
    const req = share ? axios.get(`${API}/share/${creationId}/printability`) : api.get(`/creations/${creationId}/printability`);
    req.then(({ data }) => alive && setRep(data)).catch(() => alive && setRep(false));
    return () => {
      alive = false;
    };
  }, [creationId, share]);

  if (rep === null)
    return (
      <div data-testid="print-ready-loading" className="flex items-center gap-2 rounded-2xl border border-border bg-card/60 px-4 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking print quality…
      </div>
    );
  if (rep === false) return null;

  const clean = rep.watertight && (rep.passes_walls ?? rep.passes_nozzle);
  const vol = rep.volume_cm3 ? `~${rep.volume_cm3} cm³` : null;
  const wall =
    rep.thin_area_pct == null ? null : `${(100 - rep.thin_area_pct).toFixed(1)}% walls ≥ ${rep.thin_mm} mm`;

  return (
    <div
      data-testid="print-ready-badge"
      data-status={clean ? "ready" : "caution"}
      className={`rounded-2xl border px-4 py-3 ${clean ? "border-accent/50 bg-accent/10" : "border-amber-500/50 bg-amber-500/10"}`}
    >
      <div className="flex items-center gap-2">
        {clean ? <ShieldCheck className="h-5 w-5 text-accent shrink-0" /> : <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />}
        <span data-testid="print-ready-status" className="font-bold text-sm">
          {clean ? "Print-ready ✓" : "Print-ready — minor caution"}
        </span>
        <span data-testid="print-ready-facts" className="ml-auto text-xs font-mono2 text-muted-foreground">
          {rep.watertight ? "watertight" : "sealing…"}
          {wall ? ` · ${wall}` : ""}
          {vol ? ` · ${vol}${rep.volume_height_mm ? ` at ${rep.volume_height_mm} mm` : ""}` : ""}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">
        {clean
          ? "Checked and sealed — a solid, watertight mesh with walls thick enough for a clean 0.4 mm FDM print."
          : rep.warnings?.[0] || "A few fine details are very small; they may soften slightly when printed."}
      </p>
      {rep.overhang?.needs_supports && (
        <p data-testid="print-ready-overhang" className="text-xs font-semibold text-amber-600 mt-1.5">
          {rep.overhang.message} (widest unsupported span {rep.overhang.largest_unsupported_span_mm} mm · about{" "}
          {rep.overhang.support_volume_cm3} cm³ of support) — tree supports are already switched on in the 3MF.
        </p>
      )}
      {notice?.note && (
        <p data-testid="print-ready-minor-repair" className="text-xs font-semibold text-sky-600 mt-1.5 flex items-start gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {notice.note}
        </p>
      )}
    </div>
  );
};
